<template>
  <div class="backtest-page">
    <header class="page-heading">
      <div>
        <h4>题材持续性回测</h4>
        <p>用历史收盘信号推荐题材，动态换核并保留仍在趋势中的核心股，下一交易日开盘执行。</p>
      </div>
      <span class="version" v-if="result">{{ result.engine_version }}</span>
    </header>

    <b-card class="control-card" no-body>
      <b-form @submit.prevent="runBacktest" class="control-grid">
        <b-form-group label="开始日期">
          <b-form-input v-model="form.start_date" type="date" required />
        </b-form-group>
        <b-form-group label="结束日期">
          <b-form-input v-model="form.end_date" type="date" required />
        </b-form-group>
        <b-form-group label="每日题材数">
          <b-form-input v-model.number="form.max_topics" type="number" min="1" max="20" />
        </b-form-group>
        <b-form-group label="每题材核心股">
          <b-form-input v-model.number="form.max_core_stocks" type="number" min="1" max="10" />
        </b-form-group>
        <b-form-group label="转弱确认天数">
          <b-form-input v-model.number="form.exit_confirmation_days" type="number" min="1" max="5" />
        </b-form-group>
        <b-form-group label="最低持续分">
          <b-form-input v-model.number="form.min_continuity_score" type="number" min="0" max="100" step="1" />
        </b-form-group>
        <b-form-group label="最大单股仓位">
          <b-form-input v-model.number="weightPercent" type="number" min="1" max="100" step="1" />
        </b-form-group>
        <b-form-group label="盘中时点">
          <b-form-input v-model="asOfTime" type="time" min="09:30" max="15:00" :disabled="!intraday" />
          <b-form-checkbox v-model="intraday" size="sm">单日分钟回放</b-form-checkbox>
        </b-form-group>
        <div class="run-action">
          <b-button type="submit" variant="primary" :disabled="loading">
            <b-spinner v-if="loading" small class="mr-1" />{{ loading ? '正在回测' : '运行回测' }}
          </b-button>
        </div>
      </b-form>
    </b-card>

    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>
    <template v-if="result && result.status === 'ok'">
      <b-alert show :variant="result.data_quality.strict_point_in_time ? 'success' : 'warning'" class="quality-alert">
        <strong>数据可信度：{{ result.data_quality.strict_point_in_time ? '严格历史时点' : '研究级回测' }}</strong>
        <span v-for="warning in result.data_quality.warnings" :key="warning">{{ warning }}</span>
      </b-alert>

      <section class="metrics" v-if="result.mode === 'intraday'">
        <article><small>回放时点</small><b>{{ result.trade_date }} {{ result.as_of_time }}</b></article>
        <article><small>行情覆盖</small><b>{{ result.quote_count }}</b></article>
        <article><small>推荐题材</small><b>{{ result.summary.recommendation_count }}</b></article>
        <article><small>核心股</small><b>{{ result.summary.core_stock_count }}</b></article>
        <article><small>5日样本</small><b>{{ result.summary.five_day_sample_count }}</b></article>
        <article><small>5日均值</small><b :class="numberClass(result.summary.five_day_average_return_pct)">{{ pct(result.summary.five_day_average_return_pct) }}</b></article>
        <article><small>5日胜率</small><b>{{ pct(result.summary.five_day_win_rate) }}</b></article>
      </section>

      <section class="result-card" v-if="result.mode === 'intraday'">
        <div class="section-title"><h5>该时点推荐</h5><span>收益列为盘中价买入后的事后评价</span></div>
        <div class="table-responsive">
          <table class="result-table">
            <thead><tr><th>题材</th><th>状态</th><th>持续分</th><th>排名</th><th>核心股</th><th>时点涨幅</th><th>收盘</th><th>1日</th><th>3日</th><th>5日</th></tr></thead>
            <tbody>
              <tr v-for="row in intradayRows" :key="row.topic_id + '-' + row.code">
                <td><strong>{{ row.topic_name }}</strong></td><td>{{ row.state }}</td><td>{{ number(row.continuity_score) }}</td><td>{{ row.rank ? '#' + row.rank : '--' }}</td>
                <td :title="coreReason(row)">{{ row.name }}（{{ row.code }}）<span class="core-score">{{ number(row.core_score) }}</span></td><td :class="numberClass(row.pct)">{{ pct(row.pct) }}</td>
                <td :class="numberClass(row.return_0d_pct)">{{ pct(row.return_0d_pct) }}</td><td :class="numberClass(row.return_1d_pct)">{{ pct(row.return_1d_pct) }}</td>
                <td :class="numberClass(row.return_3d_pct)">{{ pct(row.return_3d_pct) }}</td><td :class="numberClass(row.return_5d_pct)">{{ pct(row.return_5d_pct) }}</td>
              </tr>
              <tr v-if="!intradayRows.length"><td colspan="10" class="empty">该时点没有达到算法门槛的核心股推荐</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="metrics" v-if="result.mode !== 'intraday' && portfolio.summary">
        <article><small>区间收益</small><b :class="numberClass(portfolio.summary.total_return_pct)">{{ pct(portfolio.summary.total_return_pct) }}</b></article>
        <article><small>最大回撤</small><b class="down">{{ pct(portfolio.summary.max_drawdown_pct) }}</b></article>
        <article><small>期末权益</small><b>{{ money(portfolio.summary.final_equity) }}</b></article>
        <article><small>完成交易</small><b>{{ portfolio.summary.sell_count || 0 }}</b></article>
        <article><small>交易胜率</small><b>{{ pct(portfolio.summary.closed_trade_win_rate) }}</b></article>
        <article><small>5日信号均值</small><b :class="numberClass(evaluation.five_day_average_return_pct)">{{ pct(evaluation.five_day_average_return_pct) }}</b></article>
        <article><small>5日信号胜率</small><b>{{ pct(evaluation.five_day_win_rate) }}</b></article>
        <article><small>相对全题材超额</small><b :class="numberClass(evaluation.five_day_excess_return_pct)">{{ pct(evaluation.five_day_excess_return_pct) }}</b></article>
      </section>

      <section class="curve-card" v-if="result.mode !== 'intraday' && portfolio.equity_curve && portfolio.equity_curve.length">
        <div class="section-title"><h5>净值轨迹</h5><span>{{ result.parameters.start_date }} 至 {{ result.parameters.end_date }}</span></div>
        <svg viewBox="0 0 1000 190" preserveAspectRatio="none" role="img" aria-label="回测净值曲线">
          <line x1="0" y1="170" x2="1000" y2="170" class="axis" />
          <polyline :points="curvePoints" class="equity-line" fill="none" />
        </svg>
        <div class="curve-axis"><span>{{ portfolio.equity_curve[0].trade_date }}</span><span>{{ portfolio.equity_curve[portfolio.equity_curve.length - 1].trade_date }}</span></div>
      </section>

      <section class="result-card" v-if="result.mode !== 'intraday'">
        <div class="section-title"><h5>推荐效果</h5><span>{{ signals.length }} 条题材信号</span></div>
        <div class="table-responsive">
          <table class="result-table">
            <thead><tr><th>日期</th><th>题材</th><th>状态</th><th>持续分</th><th>排名</th><th>核心股</th><th>1日</th><th>3日</th><th>5日</th></tr></thead>
            <tbody>
              <tr v-for="signal in signals" :key="signal.signal_date + '-' + signal.topic_id">
                <td>{{ signal.signal_date }}</td><td><strong>{{ signal.topic_name }}</strong></td><td>{{ signal.state }}</td>
                <td>{{ number(signal.continuity_score) }}</td><td>{{ signal.rank ? '#' + signal.rank : '--' }}</td>
                <td><span v-for="stock in signal.core_stocks" :key="stock.code" class="stock-chip" :title="coreReason(stock)">{{ stock.name }} <b>{{ number(stock.core_score) }}</b></span><em v-if="!signal.core_stocks.length">未达门槛</em></td>
                <td :class="numberClass(signal.return_1d_pct)">{{ pct(signal.return_1d_pct) }}</td>
                <td :class="numberClass(signal.return_3d_pct)">{{ pct(signal.return_3d_pct) }}</td>
                <td :class="numberClass(signal.return_5d_pct)">{{ pct(signal.return_5d_pct) }}</td>
              </tr>
              <tr v-if="!signals.length"><td colspan="9" class="empty">所选区间没有满足条件的题材信号</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="result-card" v-if="result.mode !== 'intraday'">
        <div class="section-title"><h5>成交记录</h5><span>收盘判断，次日开盘执行</span></div>
        <div class="table-responsive">
          <table class="result-table compact">
            <thead><tr><th>成交日</th><th>信号日</th><th>方向</th><th>股票</th><th>成交价</th><th>数量</th><th>收益</th></tr></thead>
            <tbody>
              <tr v-for="(trade, index) in portfolio.trades" :key="index">
                <td>{{ trade.trade_date }}</td><td>{{ trade.signal_date || '--' }}</td>
                <td><b :class="trade.side === 'BUY' ? 'up' : 'down'">{{ trade.side === 'BUY' ? '买入' : '卖出' }}</b></td>
                <td>{{ trade.name }}（{{ trade.code }}）</td><td>{{ trade.price }}</td><td>{{ trade.shares }}</td>
                <td :class="numberClass(trade.return_pct)">{{ pct(trade.return_pct) }}</td>
              </tr>
              <tr v-if="!portfolio.trades.length"><td colspan="7" class="empty">暂无成交</td></tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { request_zzquant } from '@/network/request'

function isoDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default {
  name: 'WonderTopicBacktest',
  data() {
    const end = new Date()
    const start = new Date(end.getTime() - 60 * 86400000)
    return {
      loading: false,
      error: '',
      result: null,
      intraday: false,
      asOfTime: '14:30',
      weightPercent: 20,
      form: {
        start_date: isoDate(start), end_date: isoDate(end), initial_cash: 1000000,
        max_topics: 1, max_core_stocks: 1, exit_confirmation_days: 3, min_continuity_score: 0,
        rotate_core_stocks: true, core_rotation_confirmation_days: 1, core_retention_rank: 7,
        hold_core_trend: true, core_trend_ma_days: 3, core_trend_max_drawdown_pct: 10,
        core_trend_break_confirmation_days: 1,
        max_stock_weight: 0.2, commission_rate: 0.0003,
        stamp_duty_rate: 0.0005, slippage_bps: 5
      }
    }
  },
  computed: {
    portfolio() { return (this.result && this.result.portfolio) || { summary: {}, equity_curve: [], trades: [] } },
    evaluation() { return (this.result && this.result.recommendation_evaluation && this.result.recommendation_evaluation.summary) || {} },
    signals() { return (this.result && this.result.recommendation_evaluation && this.result.recommendation_evaluation.signals) || [] },
    intradayRows() {
      const recommendations = (this.result && this.result.recommendations) || []
      return recommendations.reduce((rows, topic) => rows.concat((topic.core_stocks || []).map(stock => ({
        ...stock,
        topic_id: topic.topic_id,
        topic_name: topic.topic_name,
        state: topic.state,
        continuity_score: topic.continuity_score,
        rank: topic.rank
      }))), [])
    },
    curvePoints() {
      const values = this.portfolio.equity_curve || []
      if (!values.length) return ''
      const equities = values.map(item => Number(item.equity))
      const min = Math.min(...equities)
      const max = Math.max(...equities)
      const span = max - min || 1
      return equities.map((value, index) => {
        const x = values.length === 1 ? 500 : index / (values.length - 1) * 1000
        const y = 165 - (value - min) / span * 145
        return `${x.toFixed(1)},${y.toFixed(1)}`
      }).join(' ')
    }
  },
  methods: {
    pct(value) { if (value === null || value === undefined) return '--'; const n = Number(value); return `${n > 0 ? '+' : ''}${n.toFixed(2)}%` },
    number(value) { return value === null || value === undefined ? '--' : Number(value).toFixed(1) },
    coreReason(stock) { return (stock && stock.reasons && stock.reasons.length) ? stock.reasons.join(' · ') : '动态核心评分' },
    money(value) { return value === null || value === undefined ? '--' : `¥${Number(value).toLocaleString('zh-CN', { maximumFractionDigits: 0 })}` },
    numberClass(value) { return Number(value) > 0 ? 'up' : Number(value) < 0 ? 'down' : '' },
    async runBacktest() {
      this.loading = true
      this.error = ''
      this.result = null
      try {
        const payload = {
          ...this.form,
          end_date: this.intraday ? this.form.start_date : this.form.end_date,
          as_of_time: this.intraday ? this.asOfTime : null,
          max_stock_weight: Number(this.weightPercent) / 100
        }
        const response = await request_zzquant({ url: '/v3/topic/continuity/backtest', method: 'POST', data: payload, timeout: 180000, noRedirect: true })
        const body = response && response.data
        if (!body || body.code !== 20000) throw new Error((body && body.message) || '回测失败')
        this.result = body.data
        if (this.result.status !== 'ok') this.error = this.result.message || '所选区间没有可用数据'
      } catch (error) {
        const detail = error && error.response && error.response.data && error.response.data.detail
        this.error = detail || error.message || '回测失败，请检查数据连接后重试。'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.backtest-page { padding:12px 10px 30px; color:var(--text-main,#263445); }
.page-heading,.section-title { display:flex; justify-content:space-between; align-items:flex-end; gap:12px; }
.page-heading { margin-bottom:12px; }.page-heading h4,.section-title h5 { margin:0; font-weight:700; }.page-heading p { margin:4px 0 0; color:var(--text-muted,#718096); font-size:12px; }
.version,.section-title span { color:var(--text-muted,#718096); font-size:11px; }.control-card,.result-card,.curve-card { margin-bottom:12px; border-color:var(--border-color,#dfe5eb); background:var(--bg-card,#fff); }
.control-grid { display:grid; grid-template-columns:repeat(6,minmax(110px,1fr)) auto; gap:10px; align-items:end; padding:12px; }.control-grid >>> .form-group { margin:0; }.control-grid >>> label { margin-bottom:3px; color:var(--text-muted,#718096); font-size:11px; }.run-action { padding-bottom:1px; }
.quality-alert { display:flex; flex-direction:column; gap:3px; font-size:11px; }.quality-alert strong { font-size:12px; }
.metrics { display:grid; grid-template-columns:repeat(8,minmax(110px,1fr)); gap:8px; margin-bottom:12px; }.metrics article { padding:10px 12px; border:1px solid var(--border-color,#dfe5eb); border-radius:7px; background:var(--bg-card,#fff); }.metrics small { display:block; color:var(--text-muted,#718096); font-size:10px; }.metrics b { display:block; margin-top:3px; font-size:17px; font-variant-numeric:tabular-nums; }
.section-title { padding:10px 12px; border-bottom:1px solid var(--border-color,#e8edf2); }.curve-card svg { width:100%; height:190px; padding:10px 12px 0; }.axis { stroke:var(--border-color,#e1e6eb); stroke-width:1; }.equity-line { stroke:#3478b8; stroke-width:3; vector-effect:non-scaling-stroke; }.curve-axis { display:flex; justify-content:space-between; padding:0 12px 8px; color:var(--text-muted,#718096); font-size:10px; }
.result-table { width:100%; min-width:850px; border-collapse:collapse; font-size:11px; }.result-table th,.result-table td { padding:8px 9px; border-bottom:1px solid var(--border-color,#edf0f3); text-align:left; white-space:nowrap; }.result-table th { color:var(--text-muted,#718096); font-weight:600; background:var(--bg-muted,#f6f8fa); }.result-table td:nth-child(6) { white-space:normal; }.result-table.compact { min-width:700px; }.stock-chip { display:inline-block; margin:1px 4px 1px 0; padding:2px 5px; border-radius:4px; background:var(--bg-muted,#edf2f6); }.core-score { display:inline-block; margin-left:5px; padding:1px 4px; border-radius:4px; background:#e9f2fb; color:#2f6fa8; font-size:10px; }.stock-chip b { color:#2f6fa8; font-size:10px; }.result-table em,.empty { color:var(--text-muted,#718096); font-style:normal; }.empty { text-align:center!important; padding:24px!important; }.up { color:#c84435!important; }.down { color:#269c50!important; }
@media(max-width:1100px){.control-grid{grid-template-columns:repeat(3,minmax(120px,1fr))}.metrics{grid-template-columns:repeat(4,minmax(110px,1fr))}}@media(max-width:650px){.control-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.run-action{grid-column:1/-1}.run-action .btn{width:100%}.metrics{grid-template-columns:repeat(2,minmax(0,1fr))}.page-heading{align-items:flex-start;flex-direction:column}.curve-card svg{height:145px}}
</style>
